import { SimpleSEO } from '@/components/seo/SimpleSEO';

export function PrivacyPolicy() {
  return (
    <>
      <SimpleSEO
        title="Privacy Policy | MdbApp"
        description="Privacy Policy for MdbApp Token Creator - Learn how we protect your data and privacy"
        keywords="privacy policy, data protection, MdbApp privacy"
      />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last Updated: October 16, 2025</p>

        <div className="space-y-8 text-foreground">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="mb-4">
              Welcome to MdbApp ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").
            </p>
            <p>
              By using MdbApp, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">2.1 Blockchain Wallet Information</h3>
            <p className="mb-4">
              When you connect your cryptocurrency wallet (such as MetaMask or WalletConnect) to our Service:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>We access your public wallet address</li>
              <li>We do NOT have access to your private keys or seed phrases</li>
              <li>We do NOT have the ability to initiate transactions without your explicit approval</li>
              <li>All transactions require your confirmation through your wallet provider</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.2 Usage Data</h3>
            <p className="mb-4">
              We may collect information about how you access and use the Service, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Device information (device type, operating system, browser type)</li>
              <li>IP address and general location data</li>
              <li>Pages visited and features used</li>
              <li>Time and date of visits</li>
              <li>Diagnostic and performance data</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.3 Transaction Data</h3>
            <p className="mb-4">
              When you create tokens or interact with smart contracts through our Service:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Transaction details are recorded on the blockchain (public and immutable)</li>
              <li>We may store transaction hashes for service functionality</li>
              <li>Token configurations and parameters you set</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our token creation and management services</li>
              <li><strong>User Support:</strong> To respond to your inquiries and provide customer support</li>
              <li><strong>Security:</strong> To detect, prevent, and address technical issues and fraudulent activity</li>
              <li><strong>Analytics:</strong> To understand how users interact with our Service and improve user experience</li>
              <li><strong>Communication:</strong> To send you service-related notifications and updates (with your consent)</li>
              <li><strong>Compliance:</strong> To comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Encryption of data in transit using SSL/TLS protocols</li>
              <li>Secure server infrastructure with regular security updates</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Regular security audits and monitoring</li>
            </ul>
            <p className="mb-4">
              However, please note that no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
            <p>
              <strong>Important:</strong> We never store your private keys, seed phrases, or passwords. These remain solely in your control through your wallet provider.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Third-Party Services</h2>
            <p className="mb-4">Our Service integrates with third-party services, including:</p>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">5.1 Wallet Providers</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>MetaMask:</strong> Subject to MetaMask's privacy policy</li>
              <li><strong>WalletConnect:</strong> Subject to WalletConnect's privacy policy</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.2 Blockchain Networks</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Ethereum, BNB Smart Chain, and Avalanche networks</li>
              <li>All blockchain transactions are public and permanently recorded</li>
              <li>Blockchain explorers (Etherscan, BscScan, SnowTrace)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.3 Analytics Services</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>We may use analytics services to improve our Service</li>
              <li>These services may collect and process data according to their own privacy policies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Blockchain Transparency</h2>
            <p className="mb-4">
              <strong>Important Notice:</strong> Blockchain transactions are public and permanent. When you use our Service to create tokens or interact with smart contracts:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your wallet address and transaction details are publicly visible on the blockchain</li>
              <li>This information cannot be deleted or modified once recorded</li>
              <li>Anyone can view this information using blockchain explorers</li>
              <li>We have no control over blockchain data as it is decentralized</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Your Rights and Choices</h2>
            <p className="mb-4">You have the following rights regarding your information:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your information (subject to legal obligations)</li>
              <li><strong>Opt-Out:</strong> Opt-out of marketing communications</li>
              <li><strong>Disconnect:</strong> Disconnect your wallet at any time</li>
            </ul>
            <p>
              Note: We cannot delete or modify information recorded on the blockchain, as this is outside our control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Children's Privacy</h2>
            <p>
              Our Service is not intended for users under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will take steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws different from your jurisdiction. By using our Service, you consent to such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Changes to This Privacy Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Posting the new Privacy Policy on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending you a notification (for material changes)</li>
            </ul>
            <p>
              Your continued use of the Service after changes are posted constitutes your acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
            <p className="mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <ul className="list-none space-y-2">
              <li><strong>Website:</strong> https://mdbapp.io</li>
              <li><strong>Twitter:</strong> @MobDaBoss</li>
            </ul>
          </section>

          <section className="border-t pt-8 mt-8">
            <h2 className="text-2xl font-semibold mb-4">12. Legal Disclaimer</h2>
            <p className="mb-4">
              <strong>Cryptocurrency Risks:</strong> Using cryptocurrency and blockchain technology involves risks. You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Securing your private keys and wallet credentials</li>
              <li>Understanding the risks of cryptocurrency transactions</li>
              <li>Complying with applicable laws and regulations in your jurisdiction</li>
              <li>Conducting your own research before creating or investing in tokens</li>
            </ul>
          </section>
        </div>
      </div>
    </>
  );
}
